<!-- collectionOperationDashboard/component/CityDetail.vue -->
<template>
  <div class="card table-card">
    <div class="table-tools">
      <h3>{{ $t('collectionOperationDashboard.cityDetailTitle') }}</h3>
      <div class="table-actions">
        <el-input
          v-model="keyword"
          :placeholder="$t('collectionOperationDashboard.searchPlaceholder')"
          class="search-input"
          clearable
          prefix-icon="el-icon-search"
          size="small"
          @keyup.enter.native="handleKeywordSearch"
          @clear="handleKeywordSearch"
        />
        <el-button class="export-btn" size="small" @click="handleExport">
          {{ $t('collectionOperationDashboard.exportCsv') }}
        </el-button>
      </div>
    </div>

    <div class="table-wrap">
      <el-table
        v-loading="loading"
        :data="tableData"
        height="470"
        stripe
        style="width: 100%"
      >
        <!-- 表头字段（2026-10-23 产品确认7列）：客户 / UF / 城市 / 揽收总量 / 当天装车量 / 未装车量 / 揽收率 -->
        <el-table-column :label="$t('GlobalSetObj.client')" min-width="100" prop="platform">
          <template slot-scope="{ row }">
            <span class="client-name">{{ formatPlatform(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="UF" prop="stateCode" width="80">
          <template slot-scope="{ row }">
            <span v-if="row.stateCode" class="uf-chip">{{ row.stateCode }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('collectionCenter.city')" min-width="180" prop="cityName">
          <template slot-scope="{ row }">
            <span class="city-name">{{ row.cityName || '' }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('collectionOperationDashboard.collectVolume')"
          align="right"
          prop="collectTotal"
        >
          <template slot-scope="{ row }">
            <span class="cell-num">{{ formatNumber(row.collectTotal) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('collectionOperationDashboard.loadedTotal')"
          align="right"
          prop="loadedTotal"
        >
          <template slot-scope="{ row }">
            <span class="cell-num">{{ formatNumber(row.loadedTotal) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('collectionOperationDashboard.unloadedTotal')"
          align="right"
          prop="unloadedTotal"
        >
          <template slot-scope="{ row }">
            <span class="cell-num">{{ formatNumber(row.unloadedTotal) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('collectionCenter.CollectionRate')"
          align="right"
          prop="collectRate"
        >
          <template slot-scope="{ row }">
            <span v-if="formatRate(row)" :class="rateClass(row)" class="rate">
              <i :class="rateClass(row)" class="dot" />
              {{ formatRate(row) }}
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="pager">
      <span v-if="keyword" class="filter-tip">
        {{ $t('collectionOperationDashboard.keywordFiltered', { keyword, count: queryForm.total }) }}
      </span>
      <el-pagination
        :current-page="queryForm.pageNumber"
        :page-size="queryForm.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="queryForm.total"
        background
        layout="total, sizes, prev, pager, next, jumper"
        style="margin: 10px 0; float: right"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script>
import { exportFmCollectCityDetail, getFmCollectCityDetail } from '../api';
import { normalizePlatform } from '../utils/brazilMap';
import { goResourceTaskList } from '@/utils/goExportList';
import { rateOf } from '../utils/format';

export default {
  name: 'CityDetail',
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      tableData: [],
      // 分页参数（入参 pageNumber/pageSize，响应仅返回 total）
      queryForm: {
        pageNumber: 1,
        pageSize: 20,
        total: 0
      },
      // 表格右上角搜索关键字（接口无关键词入参，仅对当前页数据做本地筛选）
      keyword: ''
    };
  },
  computed: {},
  methods: {
    getBody(res) {
      if (res && typeof res === 'object' && Object.prototype.hasOwnProperty.call(res, 'data')) {
        return res.data;
      }
      return res;
    },

    // 与父级保持一致的公共查询参数
    buildQueryParams() {
      const [startDate, endDate] = this.filters.dateRange || [];
      return {
        startDate: startDate || '',
        endDate: endDate || '',
        platform: this.filters.client || '',
        serviceType: this.filters.service || '',
        stateCode: this.filters.uf || '',
        cityName: this.filters.city || '',
        // HUB 下拉对应站点，接口入参为 siteIds 数组；未选择时传空数组表示不限
        siteIds: this.filters.hub ? [this.filters.hub] : [],
        // 页内搜索词同样下发给后端：分页与导出都按它过滤
        keyword: String(this.keyword || '').trim()
      };
    },

    // 表格搜索：回车或点清除时立即按关键字重查（回到第一页）
    handleKeywordSearch() {
      this.queryForm.pageNumber = 1;
      return this.loadCityDetail(this.buildQueryParams());
    },

    // 客户名统一归一化（后端 TKTOK → TikTok，Kwai 等 → OTHER）
    formatPlatform(row) {
      if (!row || !row.platform) return '';
      return normalizePlatform(row.platform);
    },

    // 数量：接口没给就留空
    formatNumber(num) {
      if (num === '' || num === null || num === undefined) return '';
      const n = Number(num);
      if (!Number.isFinite(n)) return '';
      return new Intl.NumberFormat('zh-CN').format(Math.round(n));
    },

    // 比率：优先用本行 装车量/揽收总量 重算，后端 collectRate 只有 1 位小数（2026-10-26 测试反馈）
    rateValue(row) {
      const computed = rateOf(row && row.loadedTotal, row && row.collectTotal);
      if (computed !== null) return computed;
      const rate = row && row.collectRate;
      if (rate === '' || rate === null || rate === undefined) return null;
      const n = Number(rate);
      return Number.isFinite(n) ? n : null;
    },

    // 表格列窄，沿用 1 位小数展示（与导出文件口径一致）
    formatRate(row) {
      const value = this.rateValue(row);
      if (value === null) return '';
      return `${value.toFixed(1)}%`;
    },

    rateClass(row) {
      const n = this.rateValue(row);
      if (n === null) return '';
      if (n < 88) return 'bad';
      if (n < 94) return 'warn';
      return '';
    },

    // 供父级“重置”时调用：清空搜索关键字与分页
    reset() {
      this.keyword = '';
      this.queryForm.pageNumber = 1;
    },

    // 供父级“查询”时统一调用（回到第一页）
    fetchData(params) {
      this.queryForm.pageNumber = 1;
      return this.loadCityDetail(params);
    },

    async loadCityDetail(params) {
      this.loading = true;
      try {
        const res = await getFmCollectCityDetail({
          pageNumber: this.queryForm.pageNumber,
          pageSize: this.queryForm.pageSize,
          params: {
            startDate: params.startDate,
            endDate: params.endDate,
            platform: params.platform,
            serviceType: params.serviceType,
            stateCode: params.stateCode,
            cityName: params.cityName,
            siteIds: params.siteIds,
            // 关键字取组件自身状态：父级「查询」传下来的参数里没有搜索词
            keyword: String(this.keyword || '').trim()
          }
        });
        console.log('[CityDetail] 城市明细 cityDetail 响应：', res);
        const body = this.getBody(res) || {};
        this.tableData = Array.isArray(body.records) ? body.records : [];
        // 接口实际返回 { records, total }（文档写的 totalRow 未返回），此处同时兼容两者
        const total =
          body.totalRow === undefined || body.totalRow === null ? body.total : body.totalRow;
        this.queryForm.total =
          total === undefined || total === null ? this.tableData.length : Number(total);
        this.queryForm.pageNumber = Number(body.pageNumber) || this.queryForm.pageNumber;
      } catch (error) {
        console.error('[CityDetail] 城市明细加载失败：', error);
      } finally {
        this.loading = false;
      }
    },

    handleCurrentChange(page) {
      this.queryForm.pageNumber = page;
      return this.loadCityDetail(this.buildQueryParams());
    },

    // 每页条数变化：回到第一页重新查询
    handleSizeChange(size) {
      this.queryForm.pageSize = size;
      this.queryForm.pageNumber = 1;
      return this.loadCityDetail(this.buildQueryParams());
    },

    // 城市明细导出（异步导出：提交后到统一导出列表下载）
    async handleExport() {
      try {
        const params = this.buildQueryParams();
        const res = await exportFmCollectCityDetail(params);
        console.log('[CityDetail] 城市明细导出提交响应：', res);
        await goResourceTaskList();
      } catch (error) {
        console.error('[CityDetail] 城市明细导出失败：', error);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.table-card {
  border: 1px solid #e1e9e5;
  border-radius: 17px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 36, 27, 0.03), 0 12px 32px rgba(16, 36, 27, 0.055);
  overflow: hidden;
  margin-top: 12px;

  .table-tools {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 17px 12px;
    gap: 10px;

    h3 {
      margin: 0;
      font-size: 15px;
    }

    .table-actions {
      display: flex;
      align-items: center;
      gap: 8px;

      .search-input {
        width: 268px;

        ::v-deep .el-input__inner {
          height: 36px;
          border: 1px solid #dbe5e0;
          border-radius: 9px;
          background: #fff;
          color: #2f453b;

          &::placeholder {
            color: #a7b5ae;
          }

          &:hover {
            border-color: #bcd8ca;
          }

          &:focus {
            border-color: #079455;
            box-shadow: 0 0 0 3px rgba(7, 148, 85, 0.1);
          }
        }

        ::v-deep .el-input__prefix {
          left: 10px;
          color: #8a9b93;
        }

        ::v-deep .el-input__icon {
          line-height: 36px;
          font-size: 15px;
        }
      }

      .export-btn {
        height: 36px;
        padding: 0 16px;
        border: 1px solid #dbe5e0;
        border-radius: 9px;
        background: #fff;
        color: #2f453b;
        font-weight: 700;

        &:hover,
        &:focus {
          border-color: #079455;
          background: #f7fdfa;
          color: #05603a;
        }
      }
    }
  }

  // 表格左右留白，避免贴边（注意：padding 必须加在包裹层上，
  // 直接给 .el-table 加 padding 会让 Element 用 clientWidth 计算列宽时溢出）
  .table-wrap {
    padding: 0 15px;
  }

  // 表头加粗：全局 11-shadcn-global-hardening.scss 里有
  // `.el-table.el-table th.el-table__cell { font-weight: 500 !important }`，必须同权重 + !important 才能覆盖。
  // 注意：该规则不能写在 `.el-table { }` 内部，否则会编译成 `.el-table[data-v] .el-table.el-table th`，
  // 需要多一层同名后代元素，实际选不中表头。
  ::v-deep .el-table.el-table th.el-table__cell,
  ::v-deep .el-table.el-table th.el-table__cell > .cell {
    font-weight: 700 !important;
    color: #344054 !important;
  }

  .el-table {
    .client-name {
      font-weight: 700;
      color: #20382d;
    }

    // 城市名：深色半粗
    .city-name {
      font-weight: 600;
      color: #1d2939;
    }

    // 数值列：灰色等宽数字
    .cell-num {
      color: #667085;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
    }

    // UF 小胶囊标签
    .uf-chip {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 26px;
      height: 18px;
      padding: 0 7px;
      border-radius: 999px;
      background: #e9f9f0;
      color: #079455;
      font-size: 10px;
      font-weight: 800;
      line-height: 18px;
    }

    .rate {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: #475467;
      font-variant-numeric: tabular-nums;

      .dot {
        width: 6px;
        height: 6px;
        background: #12b76a;
        border-radius: 50%;
        box-shadow: 0 0 0 3px rgba(18, 183, 106, 0.12);

        &.warn {
          background: #f79009;
          box-shadow: 0 0 0 3px rgba(247, 144, 9, 0.12);
        }

        &.bad {
          background: #f04438;
          box-shadow: 0 0 0 3px rgba(240, 68, 56, 0.12);
        }
      }
    }
  }

  .pager {
    overflow: hidden;
    padding: 0 17px;
    color: #76877f;
    font-size: 12px;
    background: #fcfefd;

    .filter-tip {
      float: left;
      line-height: 44px;
      font-style: normal;
      color: #f79009;
    }
  }
}
</style>
